import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormContext } from "./FormContext";
import { FormValidationTypes, IInput } from "./interfaces";

type FormProps = {
  validationMessages?: Record<FormValidationTypes, string>;
  quiet?: boolean;
};

const Form: React.FC<React.PropsWithChildren<FormProps>> = ({
  children,
  quiet,
}) => {
  const { t } = useTranslation();

  const [inputs, setInputs] = useState<IInput[]>([]);

  const validationMessages: Record<FormValidationTypes, string> =
    useMemo(() => {
      const defaultMessages: Record<FormValidationTypes, string> = {
        unrecognized: "INPUT_VALIDATION",
        required: "INPUT_VALIDATION_REQUIRED",
        min: "INPUT_NUMBER_VALIDATION_MIN_$",
        max: "INPUT_NUMBER_VALIDATION_MAX_$",
        minLength: "INPUT_TEXT_VALIDATION_MIN_LENGTH_$",
        maxLength: "INPUT_TEXT_VALIDATION_MAX_LENGTH_$",
        pattern: "INPUT_VALIDATION_PATTERN",
      };

      return defaultMessages;
    }, []);

  const prepareValidationMessage = t;

  const register = useCallback((input: IInput) => {
    setInputs((allInputs) => [...allInputs, input]);
  }, []);

  const unregister = useCallback((input: IInput) => {
    setInputs((allInputs) => {
      const arr = [...allInputs];
      const index = arr.findIndex((i) => i.name === input.name);

      if (index !== -1) {
        arr.splice(index, 1);
      }

      return arr;
    });
  }, []);

  const validate = useCallback(() => {
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];

      const validation = input.validate();
      if (!validation.valid) {
        if (prepareValidationMessage && validationMessages) {
          const errorMessage =
            (validation.errorType
              ? validationMessages[validation.errorType]
              : validationMessages[FormValidationTypes.unrecognized]) ||
            validationMessages[FormValidationTypes.unrecognized];

          if (validation.params && Object.keys(validation.params).length > 0) {
            if (validation.errorType === FormValidationTypes.pattern) {
              const msg = validation.params.message as string;
              input.setValidationError(
                prepareValidationMessage((msg || errorMessage) as any)
              );
            } else {
              input.setValidationError(
                prepareValidationMessage(
                  errorMessage as any,
                  validation.params || {}
                )
              );
            }
          } else {
            input.setValidationError(
              prepareValidationMessage(errorMessage as any)
            );
          }
        } else {
          input.setValidationError(t("INPUT_VALIDATION"));
        }
        return false;
      }
    }

    return true;
  }, [inputs, prepareValidationMessage, t, validationMessages]);

  const formData = useMemo(() => {
    return { inputs, register, unregister, validate, quiet };
  }, [inputs, register, unregister, validate, quiet]);

  return (
    <FormContext.Provider value={formData}>{children}</FormContext.Provider>
  );
};

export { Form };
